import React, { Fragment, useEffect, useRef } from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Tooltip from 'bootstrap/js/dist/tooltip';
import Icon from 'tbk-components/src/components/Icon';

export interface Frequency {
	frequency: string;
	width: number;
	left: number;
	displayedFrequency: string;
}

export interface Constellation {
	constellation: string;
	frequencies: Frequency[];
}

export interface IFrequencyData {
	rowHeading: string;
	constellations: Constellation[];
}

export interface IAntennaSelectionGraphProps extends IBaseElementProps {
	graphStartFrequency: number;
	graphEndFrequency: number;
	graphData: IFrequencyData[];
	highlightedFrequencies?: string[];
	optionalFrequencies?: string[];
	columnHeadings: string[];
	xAxisLabel: string;
}

const AntennaSelectionGraph: React.FC<IAntennaSelectionGraphProps> = ({
	graphStartFrequency,
	graphEndFrequency,
	graphData = [],
	className,
	highlightedFrequencies = [],
	optionalFrequencies = [],
	columnHeadings = ['Lower Bands', 'L-Band', 'Upper Bands'],
	xAxisLabel = 'Frequencies',
	classNames = [],
}) => {
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const tooltipTriggerList = ref.current.querySelectorAll(
			'[data-bs-toggle="tooltip"]',
		);
		const tooltipList = [...tooltipTriggerList].map(
			(tooltipTriggerEl) => new Tooltip(tooltipTriggerEl),
		);

		return () => {
			tooltipList.forEach((tooltip) => tooltip.dispose());
		};
	}, []);

	return (
		<div
			className={
				className || cn('antenna-selection-graph', ...classNames)
			}
			ref={ref}
		>
			<div className="antenna-selection-graph__container">
				<div className="antenna-selection-graph__grid">
					<div className="antenna-selection-graph__column-heading">
						{Array.isArray(columnHeadings) &&
							columnHeadings.length > 0 &&
							columnHeadings.map((columnHeading, index) => (
								<span key={index}>
									<div>{columnHeading}</div>
								</span>
							))}
					</div>
					{Array.isArray(graphData) &&
						graphData.length > 0 &&
						graphData.map(
							({ constellations, rowHeading }, rowIndex) => (
								<Fragment key={rowIndex}>
									<div
										className="antenna-selection-graph__row-heading"
										style={{
											gridRow: `span ${constellations?.length}`,
										}}
										dangerouslySetInnerHTML={{
											__html: rowHeading,
										}}
									></div>
									{Array.isArray(constellations) &&
										constellations?.length > 0 &&
										constellations.map(
											(
												{ constellation, frequencies },
												constellationIndex,
											) => (
												<Fragment
													key={constellationIndex}
												>
													<div
														className={cn(
															'antenna-selection-graph__constellation',
															rowIndex ===
																graphData?.length -
																	1 &&
																constellationIndex ===
																	constellations?.length -
																		1 &&
																'last',
														)}
													>
														{constellation}
													</div>
													<div
														className={cn(
															'antenna-selection-graph__frequencies',
															rowIndex ===
																graphData?.length -
																	1 &&
																constellationIndex ===
																	constellations?.length -
																		1 &&
																'last',
														)}
													>
														{Array.isArray(
															frequencies,
														) &&
															frequencies?.length >
																0 &&
															frequencies.map(
																(
																	{
																		displayedFrequency,
																		frequency,
																		left,
																		width,
																	},
																	frequencyIndex,
																) => (
																	<div
																		className={cn(
																			'antenna-selection-graph__frequency',
																			highlightedFrequencies?.includes(
																				frequency,
																			) &&
																				'highlighted',
																			optionalFrequencies?.includes(
																				frequency,
																			) &&
																				'optional',
																		)}
																		data-bs-toggle="tooltip"
																		data-bs-title={`${constellation}${
																			constellation !==
																			'Iridium'
																				? ` ${frequency}`
																				: ''
																		} | ${displayedFrequency}`}
																		data-bs-custom-class="antenna-selection-graph__tooltip"
																		key={
																			frequencyIndex
																		}
																		style={{
																			left: `${left}%`,
																			width: `${width}%`,
																		}}
																	>
																		{
																			frequency
																		}
																		<Icon name="info" />
																	</div>
																),
															)}
													</div>
												</Fragment>
											),
										)}
								</Fragment>
							),
						)}
					<div
						className="antenna-selection-graph__x-axis"
						style={{ gridColumn: '3' }}
					>
						<span>{graphStartFrequency} Mhz</span>
						<span>{xAxisLabel}</span>
						<span>{graphEndFrequency} Mhz</span>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AntennaSelectionGraph;
